import { AdPlatform } from '@/shared/types/enums';

/**
 * Platform-specific file validation constraints for creative uploads.
 */
export interface PlatformFileConstraints {
  /** Accepted MIME types */
  acceptedImageTypes: string[];
  acceptedVideoTypes: string[];
  /** Maximum file size in bytes */
  maxImageSizeBytes: number;
  maxVideoSizeBytes: number;
  /** Recommended dimensions as "WxH" strings */
  recommendedImageDimensions: string[];
  /** Maximum pixel dimensions */
  maxImageWidth: number;
  maxImageHeight: number;
  /** Minimum pixel dimensions */
  minImageWidth: number;
  minImageHeight: number;
  /**
   * Accepted width/height ratio bounds. Meta rejects dynamic-creative images
   * outside its supported ratios (subcode 1885558), so uploads are checked
   * up-front. Omitted = any ratio.
   */
  minImageAspectRatio?: number;
  maxImageAspectRatio?: number;
  /** Max video duration in seconds */
  maxVideoDurationSeconds: number;
}

const COMMON_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const COMMON_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];

export const PLATFORM_FILE_CONSTRAINTS: Record<AdPlatform, PlatformFileConstraints> = {
  [AdPlatform.META]: {
    acceptedImageTypes: [...COMMON_IMAGE_TYPES, 'image/gif'],
    acceptedVideoTypes: [...COMMON_VIDEO_TYPES, 'video/webm'],
    maxImageSizeBytes: 30 * 1024 * 1024, // 30 MB
    maxVideoSizeBytes: 4 * 1024 * 1024 * 1024, // 4 GB
    recommendedImageDimensions: ['1200x628', '1080x1080'],
    maxImageWidth: 8192,
    maxImageHeight: 8192,
    minImageWidth: 600,
    minImageHeight: 600,
    // Meta dynamic creative accepts 4:5 (0.8) through 1.91:1.
    minImageAspectRatio: 0.8,
    maxImageAspectRatio: 1.91,
    maxVideoDurationSeconds: 240,
  },
  [AdPlatform.GOOGLE]: {
    acceptedImageTypes: [...COMMON_IMAGE_TYPES, 'image/gif'],
    acceptedVideoTypes: [...COMMON_VIDEO_TYPES],
    maxImageSizeBytes: 5 * 1024 * 1024, // 5 MB
    maxVideoSizeBytes: 256 * 1024 * 1024, // 256 MB
    recommendedImageDimensions: ['1200x628', '1200x1200', '300x250'],
    maxImageWidth: 6000,
    maxImageHeight: 6000,
    minImageWidth: 300,
    minImageHeight: 250,
    maxVideoDurationSeconds: 180,
  },
  [AdPlatform.TWITTER]: {
    acceptedImageTypes: [...COMMON_IMAGE_TYPES, 'image/webp'],
    acceptedVideoTypes: [...COMMON_VIDEO_TYPES],
    maxImageSizeBytes: 5 * 1024 * 1024, // 5 MB
    maxVideoSizeBytes: 512 * 1024 * 1024, // 512 MB
    recommendedImageDimensions: ['800x418', '800x800'],
    maxImageWidth: 4096,
    maxImageHeight: 4096,
    minImageWidth: 600,
    minImageHeight: 335,
    maxVideoDurationSeconds: 140,
  },
  // M1 connect-only — wizard cannot reach TikTok yet. M2 will replace these
  // with the real 9:16-video constraints from `tool/ad_spec/get/`.
  [AdPlatform.TIKTOK]: {
    acceptedImageTypes: [...COMMON_IMAGE_TYPES],
    acceptedVideoTypes: [...COMMON_VIDEO_TYPES],
    maxImageSizeBytes: 5 * 1024 * 1024,
    maxVideoSizeBytes: 512 * 1024 * 1024,
    recommendedImageDimensions: ['1080x1920'],
    maxImageWidth: 4096,
    maxImageHeight: 4096,
    minImageWidth: 600,
    minImageHeight: 600,
    maxVideoDurationSeconds: 180,
  },
};

/** Fallback constraints when no platform is selected */
const DEFAULT_CONSTRAINTS: PlatformFileConstraints = {
  acceptedImageTypes: [...COMMON_IMAGE_TYPES, 'image/gif', 'image/webp'],
  acceptedVideoTypes: [...COMMON_VIDEO_TYPES],
  maxImageSizeBytes: 10 * 1024 * 1024,
  maxVideoSizeBytes: 100 * 1024 * 1024,
  recommendedImageDimensions: ['1200x628', '1080x1080'],
  maxImageWidth: 8192,
  maxImageHeight: 8192,
  minImageWidth: 300,
  minImageHeight: 250,
  maxVideoDurationSeconds: 240,
};

export function getConstraintsForPlatform(
  platform: AdPlatform | undefined,
): PlatformFileConstraints {
  if (!platform) return DEFAULT_CONSTRAINTS;
  return PLATFORM_FILE_CONSTRAINTS[platform] ?? DEFAULT_CONSTRAINTS;
}

export interface FileValidationError {
  file: File;
  reason: string;
}

/**
 * Validates a file against the given platform constraints.
 * Returns null if valid, or a validation error.
 */
export function validateFile(
  file: File,
  constraints: PlatformFileConstraints,
): FileValidationError | null {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');

  if (!isImage && !isVideo) {
    return { file, reason: 'Unsupported file type. Upload an image or video.' };
  }

  if (isImage) {
    if (!constraints.acceptedImageTypes.includes(file.type)) {
      const allowed = constraints.acceptedImageTypes
        .map((t) => t.replace('image/', '').toUpperCase())
        .join(', ');
      return { file, reason: `Image format not supported. Accepted: ${allowed}.` };
    }
    if (file.size > constraints.maxImageSizeBytes) {
      const maxMB = Math.round(constraints.maxImageSizeBytes / (1024 * 1024));
      return { file, reason: `Image exceeds ${maxMB} MB size limit.` };
    }
  }

  if (isVideo) {
    if (!constraints.acceptedVideoTypes.includes(file.type)) {
      const allowed = constraints.acceptedVideoTypes
        .map((t) => t.replace('video/', '').toUpperCase())
        .join(', ');
      return { file, reason: `Video format not supported. Accepted: ${allowed}.` };
    }
    if (file.size > constraints.maxVideoSizeBytes) {
      const maxMB = Math.round(constraints.maxVideoSizeBytes / (1024 * 1024));
      return { file, reason: `Video exceeds ${maxMB} MB size limit.` };
    }
  }

  return null;
}

/**
 * Reads a video file's duration (seconds) from its metadata. Resolves null if
 * the browser can't decode it — callers should treat that as "unknown" rather
 * than block the upload. Revokes the temporary object URL when done.
 */
export function getVideoDurationSeconds(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    const cleanup = () => URL.revokeObjectURL(url);
    video.onloadedmetadata = () => {
      cleanup();
      resolve(Number.isFinite(video.duration) ? video.duration : null);
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
    video.src = url;
  });
}

/**
 * Reads an image file's intrinsic dimensions. Resolves null if the browser
 * can't decode it — callers should treat that as "unknown" rather than block
 * the upload. Revokes the temporary object URL when done.
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const cleanup = () => URL.revokeObjectURL(url);
    image.onload = () => {
      cleanup();
      resolve(
        image.naturalWidth > 0 && image.naturalHeight > 0
          ? { width: image.naturalWidth, height: image.naturalHeight }
          : null,
      );
    };
    image.onerror = () => {
      cleanup();
      resolve(null);
    };
    image.src = url;
  });
}

/**
 * Validates a picked image's dimensions against the platform limits (min/max
 * pixels and accepted aspect-ratio range). The ad platforms reject these at
 * launch with cryptic errors (e.g. Meta dynamic creative subcode 1885558), so
 * failing at pick time saves the user a full wizard round-trip. Returns a
 * reason string when invalid, or null when within limits / undeterminable.
 */
export async function validateImageDimensions(
  file: File,
  constraints: PlatformFileConstraints,
): Promise<string | null> {
  const dimensions = await getImageDimensions(file);
  if (!dimensions) return null;
  const { width, height } = dimensions;

  if (width < constraints.minImageWidth || height < constraints.minImageHeight) {
    return `Image is ${width}x${height}px — this platform needs at least ${constraints.minImageWidth}x${constraints.minImageHeight}px.`;
  }
  if (width > constraints.maxImageWidth || height > constraints.maxImageHeight) {
    return `Image is ${width}x${height}px — this platform allows at most ${constraints.maxImageWidth}x${constraints.maxImageHeight}px.`;
  }

  const ratio = width / height;
  if (
    (constraints.minImageAspectRatio != null && ratio < constraints.minImageAspectRatio) ||
    (constraints.maxImageAspectRatio != null && ratio > constraints.maxImageAspectRatio)
  ) {
    return (
      `Image ratio ${(Math.round(ratio * 100) / 100).toFixed(2)}:1 isn't supported — ` +
      `use something between 4:5 (portrait) and 1.91:1 (landscape), like 1080x1080 or 1200x628.`
    );
  }

  return null;
}

/**
 * Validates a picked video's duration against the platform limit. Returns a
 * reason string when too long, or null when within limits / undeterminable.
 */
export async function validateVideoDuration(
  file: File,
  constraints: PlatformFileConstraints,
): Promise<string | null> {
  if (!constraints.maxVideoDurationSeconds) return null;
  const duration = await getVideoDurationSeconds(file);
  if (duration !== null && duration > constraints.maxVideoDurationSeconds) {
    return `Video is ${Math.round(duration)}s — limit is ${constraints.maxVideoDurationSeconds}s for this platform.`;
  }
  return null;
}

/**
 * Formats file size in human-readable form.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Builds the accept string for the file input element.
 */
export function buildAcceptString(constraints: PlatformFileConstraints): string {
  return [...constraints.acceptedImageTypes, ...constraints.acceptedVideoTypes].join(',');
}

/**
 * Builds a human-readable hint string for accepted formats and size limits.
 */
export function buildHintText(constraints: PlatformFileConstraints): string {
  const imageFormats = constraints.acceptedImageTypes
    .map((t) => t.replace('image/', '').toUpperCase())
    .join(', ');
  const videoFormats = constraints.acceptedVideoTypes
    .map((t) => t.replace('video/', '').toUpperCase())
    .join(', ');
  const maxImageMB = Math.round(constraints.maxImageSizeBytes / (1024 * 1024));
  const maxVideoMB = Math.round(constraints.maxVideoSizeBytes / (1024 * 1024));

  return `Images: ${imageFormats} (max ${maxImageMB} MB). Videos: ${videoFormats} (max ${maxVideoMB} MB).`;
}
